import { useEffect, useRef } from "preact/hooks";

type UseModalDialogOptions = {
  // Controlled mode: the hook calls showModal()/close() to match this flag.
  // Omit for uncontrolled mode — the dialog opens once on mount and the
  // consumer unmounts it in response to onClose (Esc / backdrop / .close()).
  open?: boolean;
  onClose: () => void;
};

// Shared <dialog> plumbing extracted from the modal call sites: it mirrors the
// native `close` event back to onClose and drives showModal()/close().
// showModal() is what gives Esc handling + focus trap natively, and the
// returned ref is attached to the <dialog> element.
export function useModalDialog({ open, onClose }: UseModalDialogOptions) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose();
    el.addEventListener("close", handler);
    return () => el.removeEventListener("close", handler);
  }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open === undefined) {
      // Uncontrolled: show once on mount.
      if (!el.open) el.showModal();
    } else if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  return ref;
}
